import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Trans } from "@lingui/macro";
import { useInputChange } from "hooks/useInputChange";
import { InputText } from "components/UI/InputText/InputText";
import { Button } from "components/UI/Button/Button";
import { dPosConfigurationAddItemSagaAC } from "model/dashboardPosConfiguration/dPosConfigurationActionCreators";
import {
  addNotificationAC,
  logoutSagaAC,
} from "model/system/systemActionCreators";

import { IdPosConfigurationState } from "model/dashboardPosConfiguration/dPosConfigurationReducer";
import { Loader } from "components/UI/Loader/Loader";
import {
  InputCheckList,
  typeInputCheckboxItem,
} from "components/UI/InputCheckboxList/InputCheckList";
import {
  getLockPOSList,
  getLockStoreList,
  getPosConfigurationItemByName,
  getPosList,
  getProductListSlim,
  getStoresListSlim,
} from "model/dashboardPosConfiguration/dPosConfigurationAPI";
import {
  PencilIcon,
  TrashIcon,
  PlusIcon,
  MinusSmIcon,
} from "@heroicons/react/outline";
import { getColor } from "components/utils";
import { getCategoriesListSlim } from "model/dashboardCategories/dCategoriesAPI";
import { InputCheckbox } from "components/UI/InputCheckbox/InputCheckbox";

// Types
export type typePopupAdd = {
  variant?: string;
  setOpenPopup: React.Dispatch<React.SetStateAction<boolean>>;
};

type typeCategoryItem = {
  order: number;
  name: string;
  productIds: string[];
  isSelected: boolean;
  isEditing: boolean;
};
type typeProductListItem = {
  selected: boolean;
  id: string;
  name: string;
  products: string[];
};

export type typeAddPosConfigurationItemObject = {
  name: string;
  storeIds: string[];
  posIds: string[];
  posCategories: {
    name: string;
    productIds: string[];
  }[];
};

// Component
export const PopupAdd: React.FC<typePopupAdd> = ({ variant, setOpenPopup }) => {
  const color = getColor(variant);

  const dispatch = useDispatch();

  // System data
  const filters = useSelector(
    (state: { dPosConfiguration: IdPosConfigurationState }) =>
      state.dPosConfiguration.filters
  );
  const [storeLoader, setStoreLoader] = useState(false);
  // Did mount
  useEffect(() => {
    const setData = async () => {
      setStoreLoader(true);
      let responseStores = await getStoresListSlim();
      let responseLockStores = await getLockStoreList();

      if (responseStores.status === 200) {
        const newStoresList = [
          ...selectStoreList,
          ...responseStores.data.map((item: typeInputCheckboxItem) => ({
            id: item.id,
            name: item.name,
            label: item.name,
            checked: false,
            disabled: responseLockStores.data.includes(item.id),
          })),
        ];
        setSelectStoreList(newStoresList);
      } else if (responseStores.status === 401) {
        dispatch(logoutSagaAC());
      }

      let responsePos = await getPosList();
      let responseLockPOS = await getLockPOSList();

      if (responsePos.status === 200) {
        const filteredActivePost = responsePos.data.filter(
          (item: any) => item.active
        );
        const newPosList = [
          ...selectPOSList,
          ...filteredActivePost.map(
            (item: { id: string; posHardwareSerialNumber: string }) => ({
              id: item.id,
              name: item.posHardwareSerialNumber,
              label: item.posHardwareSerialNumber,
              checked: false,
              disabled: responseLockPOS.data.includes(item.id),
            })
          ),
        ];
        setSelectPOSList(newPosList);
      } else if (responsePos.status === 401) {
        dispatch(logoutSagaAC());
      }

      let responseCategoryList = await getCategoriesListSlim();

      if (responseCategoryList.status === 200) {
        const newCategoryList = [
          ...productList,
          ...responseCategoryList.data.map((item: typeProductListItem) => ({
            id: item.id,
            name: item.name,
            products: [],
          })),
        ];
        setProductList(newCategoryList);
      } else if (responseCategoryList.status === 401) {
        dispatch(logoutSagaAC());
      }
      setStoreLoader(false);
    };
    setData();

    // eslint-disable-next-line
  }, []);

  // Form data
  const { value: nameValue, changeValueHandler: nameChangeValueHandler } =
    useInputChange("");

  const [loader, setLoader] = useState(false);

  const [selectStoreList, setSelectStoreList] = useState<
    typeInputCheckboxItem[]
  >([]);
  const [selectPOSList, setSelectPOSList] = useState<typeInputCheckboxItem[]>(
    []
  );

  // Categories
  const {
    value: newCategoryValue,
    changeValueHandler: changeNewCategoryValue,
    setValue: setNewCategoryValue,
  } = useInputChange("");
  const [categoryList, setCategoryList] = useState<typeCategoryItem[]>([]);
  const [uniqueCategory, setUniqueCategory] = useState(true);
  const [selectedCategoryName, setSelectedCategoryName] = useState<
    string | undefined
  >(undefined);
  const [uniqueEditingCategory, setEditingUniqueCategory] = useState(true);
  const [uniqueEditingCategoryOrder, setEditingUniqueCategoryOrder] = useState<
    number | null
  >(null);
  const {
    value: editingCategoryValue,
    changeValueHandler: editingCategoryChangeValueHandler,
    setValue: setEditingCategoryValue,
  } = useInputChange("");

  useEffect(() => {
    if (categoryList.some((item) => item.name === newCategoryValue)) {
      setUniqueCategory(false);
    } else {
      setUniqueCategory(true);
    }
    // eslint-disable-next-line
  }, [newCategoryValue]);

  useEffect(() => {
    if (
      categoryList.some(
        (item) =>
          item.name === editingCategoryValue &&
          item.order !== uniqueEditingCategoryOrder
      )
    ) {
      setEditingUniqueCategory(false);
    } else {
      setEditingUniqueCategory(true);
    }
    // eslint-disable-next-line
  }, [editingCategoryValue]);

  const onAddCategoryHandler = () => {
    const newCategoryList = categoryList;
    const newCategory: typeCategoryItem = {
      order: categoryList.length
        ? categoryList[categoryList.length - 1].order + 1
        : 1,
      name: newCategoryValue,
      productIds: [],
      isSelected: false,
      isEditing: false,
    };
    newCategoryList.push(newCategory);
    setCategoryList(newCategoryList);
    setNewCategoryValue("");
    onSelectCategoryHandler(newCategory.name);
  };

  const onDeleteCategoryHandler = (name: string, order: number) => {
    const newCategoryList = categoryList
      .map((item) =>
        item.order >= order ? { ...item, ...{ order: item.order - 1 } } : item
      )
      .filter((item) => item.name !== name);
    setCategoryList(newCategoryList);
  };

  const onStartEditCategoryHandler = (name: string, order: number) => {
    const newCategoryList = categoryList.map((item) =>
      item.name === name
        ? { ...item, ...{ isEditing: true } }
        : { ...item, ...{ isEditing: false } }
    );
    setCategoryList(newCategoryList);
    setEditingCategoryValue(name);
    setEditingUniqueCategoryOrder(order);
  };

  const onSaveEditCategoryHandler = (name: string) => {
    const newCategoryList = categoryList.map((item) =>
      item.name === name
        ? {
            ...item,
            ...{
              name: editingCategoryValue,
              isEditing: false,
            },
          }
        : item
    );
    setCategoryList(newCategoryList);
    setEditingUniqueCategoryOrder(null);
    setSelectedCategoryName(editingCategoryValue);
  };

  const onKeyDownEnter = (
    e: React.KeyboardEvent<HTMLInputElement>,
    name: string
  ) => {
    if (e.key === "Enter" && uniqueEditingCategory)
      onSaveEditCategoryHandler(name);
  };

  const onCancelEditCategoryHandler = () => {
    const newCategoryList = categoryList.map((item) => ({
      ...item,
      ...{ isEditing: false },
    }));
    setCategoryList(newCategoryList);
    setEditingUniqueCategoryOrder(null);
  };

  const onSelectCategoryHandler = async (name: string) => {
    setSelectedCategoryName(name);

    const newCategoryList = categoryList.map((item) =>
      item.name === name
        ? { ...item, ...{ isSelected: true } }
        : { ...item, ...{ isSelected: false } }
    );
    setCategoryList(newCategoryList);

    const newProductList = productList.map((item) => ({
      ...item,
      ...{ selected: false },
    }));
    setProductList(newProductList);

    setSelectedProductList(true);
  };

  const [currentDrugCategoryValue, setCurrentDrugCategoryValue] =
    useState<typeCategoryItem | null>(null);

  const onDragStartHandler = (
    e: React.DragEvent<HTMLDivElement>,
    category: typeCategoryItem
  ) => {
    setCurrentDrugCategoryValue(category);
  };

  const onDragOverHandler = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const onDropHandler = (
    e: React.DragEvent<HTMLDivElement>,
    category: typeCategoryItem
  ) => {
    e.preventDefault();
    const newCategoryList = categoryList.map((item) => {
      if (item.name === category.name)
        return {
          ...item,
          ...{
            name: currentDrugCategoryValue!.name,
            productIds: currentDrugCategoryValue!.productIds,
          },
        };
      if (item.name === currentDrugCategoryValue!.name)
        return {
          ...item,
          ...{
            name: category.name,
            productIds: category.productIds,
          },
        };
      return item;
    });
    setCategoryList(newCategoryList);
  };

  // Products
  const [selectedProductList, setSelectedProductList] = useState(false);
  const [tempProductListLoader, setTempProductListLoader] = useState(false);
  const [productList, setProductList] = useState<typeProductListItem[]>([]);
  const [tempProductList, setTempProductList] = useState<
    typeInputCheckboxItem[]
  >([]);

  const [noShow, setNoShow] = useState(false);

  const onProductCategorySelect = async (
    productCategoryId: string,
    isSelected: boolean
  ) => {
    if (isSelected) {
      setNoShow(!noShow);
    } else {
      setNoShow(true);

      const newProductList = productList.map((item) =>
        item.id === productCategoryId
          ? { ...item, ...{ selected: true } }
          : { ...item, ...{ selected: false } }
      );

      setProductList(newProductList);

      setTempProductListLoader(true);

      let responseProducts = await getProductListSlim({
        categoryId: productCategoryId,
      });

      if (responseProducts.status === 200) {
        const currentCategory = categoryList.find(
          (item) => item.name === selectedCategoryName
        );
        const newTempProductList = responseProducts?.data?.content?.map(
          (item: any) => ({
            id: item.id,
            name: item.name,
            label: item.name,
            checked: currentCategory!.productIds.includes(item.id),
          })
        );
        setTempProductList(newTempProductList);
      } else if (responseProducts.status === 401) {
        dispatch(logoutSagaAC());
      }

      setTempProductListLoader(false);
    }
  };

  const onProductSelect = (productId: string, checked: boolean) => {
    const newCategory = categoryList.find(
      (item) => item.name === selectedCategoryName
    );
    if (newCategory) {
      const newProductIds = newCategory.productIds;
      if (checked) {
        newProductIds.push(productId);
      } else {
        const delItemIndex = newProductIds.indexOf(productId);
        newProductIds.splice(delItemIndex, 1);
      }
      newCategory.productIds = newProductIds;
      setCategoryList(
        categoryList.map((item) =>
          item.name === newCategory.name ? newCategory : item
        )
      );
    }
  };

  // Validation
  const [validationObj, setValidationObj] = useState({
    requiredName: false,
    uniqueName: true,
  });

  useEffect(() => {
    if (nameValue.trim() === "") {
      setValidationObj({
        ...validationObj,
        ...{ requiredName: false, uniqueName: true },
      });
    } else {
      setValidationObj({
        ...validationObj,
        ...{ requiredName: true, uniqueName: true },
      });
    }
    // eslint-disable-next-line
  }, [nameValue]);

  // Add configuration
  const onAddHandler = async (e: React.FormEvent) => {
    e.preventDefault();

    // Unique validation helper
    const uniqueValidate = (response: any) => {
      let validationValue = false;

      if (response.status === 200) {
        validationValue = false;
      } else {
        if (response.status === 404) {
          validationValue = true;
        } else if (response.status === 401) {
          dispatch(logoutSagaAC());
        } else {
          const id = new Date();
          addNotificationAC({
            id: id.getTime().toString(),
            message: "ERROR_SERVER_CONNECTION",
          });
        }
      }
      return validationValue;
    };

    setLoader(true);

    // Validation Name
    let validName;
    let response = await getPosConfigurationItemByName({
      itemName: nameValue.trim(),
    });

    validName = uniqueValidate(response);

    setValidationObj({ ...validationObj, ...{ uniqueName: validName } });

    setLoader(false);

    if (validName) {
      const dataAddObject: typeAddPosConfigurationItemObject = {
        name: nameValue.trim(),
        storeIds: selectStoreList
          .filter((item) => item.checked)
          .map((item) => item.id),
        posIds: selectPOSList
          .filter((item) => item.checked)
          .map((item) => item.id),
        posCategories: categoryList.map((item) => ({
          name: item.name,
          productIds: item.productIds,
        })),
      };
      dispatch(dPosConfigurationAddItemSagaAC(dataAddObject, filters));
      setOpenPopup(false);
    }
  };

  return (
    <form onSubmit={(e) => onAddHandler(e)}>
      <div className="relative">
        <div className="relative grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <InputText
              id="name"
              name="name"
              value={nameValue}
              label={<Trans>Configuration name</Trans>}
              onChange={nameChangeValueHandler}
              errorMessage={
                nameValue ? (
                  validationObj.uniqueName ? undefined : (
                    <Trans>This configuration name already exists</Trans>
                  )
                ) : (
                  <Trans>Required field</Trans>
                )
              }
              labelClassName="text-left break-all"
              required={true}
              maxLength={100}
            />
          </div>
          {storeLoader ? (
            <Loader />
          ) : (
            <>
              <div>
                {selectStoreList && selectStoreList.length ? (
                  <InputCheckList
                    listLabelClassName="text-left"
                    labelClassName="text-left break-all"
                    fieldsetClassName="max-h-60"
                    listLabel={<Trans>Stores</Trans>}
                    dataList={selectStoreList}
                    setDataList={setSelectStoreList}
                  />
                ) : (
                  <div className="text-gray-400 mt-5 text-center">
                    <Trans>Empty store list</Trans>
                  </div>
                )}
              </div>

              <div>
                {selectPOSList && selectPOSList.length ? (
                  <InputCheckList
                    listLabelClassName="text-left"
                    labelClassName="text-left break-all"
                    fieldsetClassName="max-h-60"
                    listLabel={<Trans>POS SN</Trans>}
                    dataList={selectPOSList}
                    setDataList={setSelectPOSList}
                  />
                ) : (
                  <div className="text-gray-400 mt-5 text-center">
                    <Trans>Empty POS list</Trans>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        <div className="mt-6">
          <span className="mb-1 block text-sm font-medium text-gray-700 text-left">
            <Trans>Categories</Trans>
          </span>
          <div className="flex gap-x-2">
            <div className="flex-grow">
              <InputText
                id="newCategory"
                value={newCategoryValue}
                errorMessage={
                  uniqueCategory ? undefined : (
                    <Trans>This category name already exist</Trans>
                  )
                }
                onChange={changeNewCategoryValue}
                maxLength={100}
              />
            </div>
            <div className="justify-items-start">
              <Button
                type="button"
                disabled={!newCategoryValue.trim() || !uniqueCategory}
                secondary={true}
                label={<Trans>Add category</Trans>}
                onClick={() => onAddCategoryHandler()}
              />
            </div>
          </div>
        </div>

        <div className="mt-4 flex border rounded-md divide-x">
          <div className="flex-grow w-80 h-80 overflow-y-auto">
            {categoryList && categoryList.length ? (
              <div className="flex flex-col">
                {categoryList.map((item, index: number) => (
                  <div
                    draggable={true}
                    onDragStart={(e) => onDragStartHandler(e, item)}
                    onDragOver={(e) => onDragOverHandler(e)}
                    onDrop={(e) => onDropHandler(e, item)}
                    className={`py-2 px-4 hover:bg-${
                      item.isSelected ? color : "gray"
                    }-50 border-b flex break-all gap-x-2 items-center${
                      item.isSelected ? ` bg-${color}-50` : ""
                    }`}
                    key={index}
                  >
                    <span className="block text-gray-400 mr-1">
                      {item.order}
                    </span>
                    {item.isEditing ? (
                      <div className="flex-grow">
                        <InputText
                          autoFocus={true}
                          id="editingCategory"
                          value={editingCategoryValue}
                          onChange={editingCategoryChangeValueHandler}
                          onBlur={() => onCancelEditCategoryHandler()}
                          errorMessage={
                            uniqueEditingCategory ? undefined : (
                              <Trans>This category name already exist</Trans>
                            )
                          }
                          onKeyDown={(e) => onKeyDownEnter(e, item.name)}
                          maxLength={100}
                        />
                      </div>
                    ) : (
                      <span
                        className="block flex-grow cursor-pointer text-left"
                        onClick={() => onSelectCategoryHandler(item.name)}
                      >
                        {item.name}
                      </span>
                    )}
                    {item.isEditing ? null : (
                      <>
                        <PencilIcon
                          className="block h-5 w-5 text-gray-300 cursor-pointer hover:text-gray-900"
                          onClick={() =>
                            onStartEditCategoryHandler(item.name, item.order)
                          }
                        />
                        <TrashIcon
                          className="block h-5 w-5 text-gray-300 cursor-pointer hover:text-gray-900"
                          onClick={() =>
                            onDeleteCategoryHandler(item.name, item.order)
                          }
                        />
                      </>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <span className="mt-2 block text-center text-gray-400">
                <Trans>The category list is empty</Trans>
              </span>
            )}
          </div>
          <div className="relative flex-grow w-80 h-80 overflow-y-auto">
            {productList && productList.length && selectedProductList ? (
              <>
                {productList.map((item, index) => (
                  <div key={index} className="border-b">
                    <div
                      className={`py-2 px-4 flex gap-x-2 items-center cursor-pointer${
                        item.selected ? ` bg-${color}-50 border-b` : ""
                      } hover:bg-${item.selected ? color : "gray"}-50`}
                      onClick={() =>
                        onProductCategorySelect(item.id, item.selected)
                      }
                    >
                      <span className="flex-grow break-all">{item.name}</span>
                      {item.selected && noShow ? (
                        <MinusSmIcon className={`h-5 w-5 text-gray-500`} />
                      ) : (
                        <PlusIcon className={`h-5 w-5 text-gray-500`} />
                      )}
                    </div>
                    {item.selected && noShow ? (
                      <div className="relative bg-gray-20 flex flex-col divide-y">
                        {!tempProductListLoader ? (
                          tempProductList && tempProductList.length ? (
                            <>
                              {tempProductList.map(
                                (itemProduct, indexProduct) => (
                                  <div key={indexProduct} className="px-2 py-1">
                                    <div className="flex items-center">
                                      <InputCheckbox
                                        id={itemProduct.id}
                                        label={itemProduct.name}
                                        labelClassName={"break-all"}
                                        defaultChecked={itemProduct.checked}
                                        onChange={(e) =>
                                          onProductSelect(
                                            itemProduct.id,
                                            e.target.checked
                                          )
                                        }
                                      />
                                    </div>
                                  </div>
                                )
                              )}
                            </>
                          ) : (
                            <span className="block py-2 px-4 text-center text-gray-400">
                              <Trans>This category is empty</Trans>
                            </span>
                          )
                        ) : (
                          <span className="block py-2 px-4 text-center text-xs text-gray-400">
                            <Trans>Loading...</Trans>
                          </span>
                        )}
                      </div>
                    ) : null}
                  </div>
                ))}
              </>
            ) : productList.length === 0 ? (
              <span className="mt-2 block text-center text-gray-400">
                <Trans>Empty category list (create categories)</Trans>
              </span>
            ) : (
              <span className="mt-2 block text-center text-gray-400">
                <Trans>Select category</Trans>
              </span>
            )}
          </div>
        </div>

        {loader ? <Loader /> : null}
      </div>

      <div className="flex gap-4 mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
        <Button
          disabled={!nameValue.trim() || loader}
          type="submit"
          label={<Trans>Add</Trans>}
        />
        <Button
          type="button"
          secondary={true}
          label={<Trans>Cancel</Trans>}
          onClick={() => setOpenPopup(false)}
        />
      </div>
    </form>
  );
};
